import { IPromise, IScope, ITimeoutService } from "angular";
import { StateService, StateParams } from "@uirouter/angularjs";
import dayjs from "@gtmhub/dayjs";
import { ICollection, IGtmhubRootScopeService } from "../models";
import { IParameter } from "./models";
import { ParameterService } from "./parameterService";
import { IField } from "../entities/models/models";
import { EntityService } from "../entities/services/EntityService";
import { IStateInit } from "@gtmhub/core/routing";
import { UIErrorHandlingService } from "@gtmhub/error-handling";
import { convertPeriodStringToObject, convertDateRangeObjectToString, ITimePeriod } from "@gtmhub/insights";

export interface IParametersScope extends IScope {
  indicators: {
    parameters?: boolean;
  };
  parameters: ICollection<IParameter>;
  delete(parameter: IParameter, index: number): void;
}

export class ParametersCtrl implements IStateInit {
  public static $inject = ["$scope", "$timeout", "ParameterService", "UIErrorHandlingService"];

  constructor(
    private $scope: IParametersScope,
    private $timeout: ITimeoutService,
    private parameterService: ParameterService,
    private uiErrorHandlingService: UIErrorHandlingService
  ) {
    this.$scope.$on("parametersChanged", this.onParametersChanged);
    this.$scope.delete = this.delete;
    this.$scope.indicators = {};
  }

  public stateInit(): IPromise<unknown> {
    return this.init();
  }

  private init = (): IPromise<unknown> => {
    return this.loadParameters();
  };

  private onParametersChanged = () => {
    this.loadParameters();
  };

  private loadParameters = (): IPromise<unknown> => {
    this.$scope.indicators.parameters = true;

    return this.parameterService.getParameters().then(
      (parameters) => {
        this.$scope.indicators.parameters = false;
        this.$scope.parameters = parameters;
      },
      (err) => this.uiErrorHandlingService.handleIndicator(err, { indicator: "parameters", inline: true })
    );
  };

  private delete = (parameter: IParameter, index: number) => {
    this.$scope.parameters.items[index].ui = [];
    this.$scope.parameters.items[index].ui.deleting = true;

    this.parameterService.deleteParameter(parameter.id).then(
      () => {
        this.$scope.parameters.items[index].ui.deleted = true;

        this.$timeout(() => {
          this.$scope.parameters.items.splice(index, 1);
        }, 500);
      },
      (err) => {
        this.$scope.parameters.items[index].ui.deleting = false;
        this.uiErrorHandlingService.handleModal(err);
      }
    );
  };
}

interface IUIEntity {
  id: string;
  name: string;
  fields: IField[];
}

export interface IDefineParameterScope extends IScope {
  indicators: {
    saving?: boolean;
    parameter?: boolean;
  };
  parameter: IParameter;
  mode: string;
  temporaryDateRange: ITimePeriod;
  isDynamicParameter: boolean;
  selectedEntity: IUIEntity;
  selectedEntityTitleField: IField;
  selectedEntityValueField: IField;
  useDefaultValue: boolean;
  usePredefinedList: boolean;
  entities: IUIEntity[];
  errorMessage: string;
  removeDefaultValue(): void;
  toggleDefaultValue(): void;
  toggleDynamicParameter(): void;
  selectorChange(): void;
  applyDate(): void;
  create(): void;
  update(): void;
  addItem(): void;
  removeItem(index: number): void;
}

export class DefineParameterCtrl {
  public static $inject = ["$rootScope", "$scope", "$state", "$stateParams", "ParameterService", "EntityService", "UIErrorHandlingService"];

  constructor(
    private $rootScope: IGtmhubRootScopeService,
    private $scope: IDefineParameterScope,
    private $stateService: StateService,
    private $stateParams: StateParams,
    private parameterService: ParameterService,
    private entityService: EntityService,
    private uiErrorHandlingService: UIErrorHandlingService
  ) {
    this.$scope.create = this.create;
    this.$scope.indicators = {};
    this.$scope.update = this.update;
    this.$scope.addItem = this.addItem;
    this.$scope.selectorChange = this.selectorChange;
    this.$scope.applyDate = this.applyDate;
    this.$scope.removeItem = this.removeItem;
    this.$scope.removeDefaultValue = this.removeDefaultValue;
    this.$scope.toggleDefaultValue = this.toggleDefaultValue;
    this.$scope.toggleDynamicParameter = this.toggleDynamicParameter;

    this.init();
  }

  private removeDefaultValue = () => {
    this.$scope.parameter.defaultValue = "";
  };

  private applyDate = () => {
    this.$scope.parameter.defaultValue = convertDateRangeObjectToString(this.$scope.temporaryDateRange);
  };

  private toggleDefaultValue = () => {
    if (this.$scope.useDefaultValue) {
      this.$scope.useDefaultValue = false;
      this.removeDefaultValue();
    } else {
      this.$scope.useDefaultValue = true;
    }
  };

  private toggleDynamicParameter = () => {
    this.$scope.isDynamicParameter = !this.$scope.isDynamicParameter;
  };

  private selectorChange = () => {
    this.$scope.parameter.defaultValue = "";
    this.$scope.useDefaultValue = false;
    this.$scope.isDynamicParameter = false;

    this.$scope.usePredefinedList = this.$scope.parameter.selectorType === "list" || this.$scope.parameter.selectorType === "multiselect";
  };

  private init = () => {
    this.$scope.parameter = {
      defaultValue: "",
      key: "",
      predefinedValues: [],
      title: "",
      selectorType: "text",
    };

    this.$scope.temporaryDateRange = {
      startDate: dayjs().toISOString(),
      endDate: dayjs().add(1, "M").toISOString(),
    };

    if (typeof this.$stateParams.id === "undefined") {
      this.$scope.mode = "create";
      this.$scope.useDefaultValue = false;
    } else {
      this.$scope.mode = "edit";
    }

    if (this.$scope.mode === "edit") {
      this.loadParameter().then(() => {
        this.loadEntities();
      });
    } else {
      this.loadEntities();
    }
  };

  private create = () => {
    this.$scope.indicators.saving = true;
    this.fillParameterProperties();
    this.handleSavingPromise(this.parameterService.defineParameter(this.$scope.parameter));
  };

  private update = () => {
    this.$scope.indicators.saving = true;
    this.fillParameterProperties();
    this.handleSavingPromise(this.parameterService.updateParameter(this.$scope.parameter));
  };

  private handleSavingPromise = (promise: IPromise<IParameter>) => {
    promise.then(
      () => {
        this.$scope.indicators.saving = false;
        this.$rootScope.$broadcast("parametersChanged", true);
        this.$stateService.go("^.^");
      },
      (error) => {
        this.uiErrorHandlingService.handleIndicator(error, { indicator: "saving", inline: true });
      }
    );
  };

  private addItem = () => {
    this.$scope.parameter.predefinedValues.push("");
  };

  private removeItem = (index: number) => {
    this.$scope.parameter.predefinedValues.splice(index, 1);
  };

  private loadParameter = () => {
    this.$scope.indicators.parameter = true;
    const loadParameterPromise = this.parameterService.getParameter(this.$stateParams.id);
    loadParameterPromise.then(
      (parameter) => {
        setTimeout(() => document.getElementById("insightboard_parameter_title").focus());
        this.$scope.indicators.parameter = false;
        this.$scope.parameter = parameter;

        if (this.$scope.parameter.selectorType === "daterange" && this.$scope.parameter.defaultValue !== "") {
          this.$scope.temporaryDateRange = convertPeriodStringToObject(this.$scope.parameter.defaultValue);
        }

        if (this.$scope.parameter.defaultValue !== "") {
          this.$scope.useDefaultValue = true;
        }

        if (this.$scope.parameter.entityName && this.$scope.parameter.entityValueField && this.$scope.parameter.entityTitleField) {
          this.$scope.isDynamicParameter = true;
        }
      },
      (err) => this.uiErrorHandlingService.handleIndicator(err, { indicator: "parameter", inline: true })
    );
    return loadParameterPromise;
  };

  private loadEntities = () => {
    this.entityService.getSomeEntities<IUIEntity>({ fields: "id,name,fields" }).then(
      (entities) => {
        this.$scope.entities = entities;

        for (const entity of entities) {
          if (entity.name === this.$scope.parameter.entityName) {
            this.$scope.selectedEntity = entity;
          }
        }

        if (this.$scope.selectedEntity && this.$scope.selectedEntity.fields) {
          for (const field of this.$scope.selectedEntity.fields) {
            if (field.name === this.$scope.parameter.entityTitleField) {
              this.$scope.selectedEntityTitleField = field;
            }
            if (field.name === this.$scope.parameter.entityValueField) {
              this.$scope.selectedEntityValueField = field;
            }
          }
        }
      },
      (error) => {
        this.uiErrorHandlingService.handleIndicator(error, { indicator: "entities", inline: true });
      }
    );
  };

  private fillParameterProperties = () => {
    const selectedEntity = this.$scope.selectedEntity;
    if (selectedEntity && this.$scope.isDynamicParameter) {
      this.$scope.parameter.entityName = selectedEntity.name;
    } else {
      this.$scope.parameter.entityName = "";
    }
    const selectedEntityValueField = this.$scope.selectedEntityValueField;
    if (selectedEntityValueField && this.$scope.isDynamicParameter) {
      this.$scope.parameter.entityValueField = selectedEntityValueField.name;
    } else {
      this.$scope.parameter.entityValueField = "";
    }
    const selectedEntityTitleField = this.$scope.selectedEntityTitleField;
    if (selectedEntityTitleField && this.$scope.isDynamicParameter) {
      this.$scope.parameter.entityTitleField = selectedEntityTitleField.name;
    } else {
      this.$scope.parameter.entityTitleField = "";
    }
  };
}
